import { el, qs, qsa } from '../utils/dom.js';
import { TileTagger } from '../generators/TileTagger.js';
import { TileMeta, ROLE, AUTOTILE_NONE, AUTOTILE_4BIT, AUTOTILE_47, AUTOTILE_ROAD } from '../generators/TileMeta.js';
import { AutotileTemplate } from './AutotileTemplate.js';

/**
 * TileTaggerPanel — Guided UI for teaching generators about tileset tiles.
 *
 * Redesigned around a step-by-step workflow:
 *   Step 1: Tag tiles with roles (what IS this tile?)
 *   Step 2: Group terrain tiles (which tiles are variants of each other?)
 *   Step 3: Define structures (walls, floors, doors)
 *   Step 4: Define objects & decorations
 *
 * Each step has clear instructions and visual feedback.
 */
export class TileTaggerPanel {
  constructor(state, container) {
    this.state = state;
    this.container = container;
    this.tagger = null;
    this.tilesetImage = null;
    this.tilesetIndex = 0;
    this.selectedTiles = new Set();
    this.currentStep = 'tag';
    this._canvas = null;
    this._ctx = null;
    this._onClose = null;
    this._autotileTemplate = null; // active AutotileTemplate instance
    this._tileProperties = new Map(); // tileId → { passable, blocking, occluding, layer }
  }

  open(tilesetIndex, existingMeta = null) {
    const tilesets = this.state.get('tilesets');
    const ts = tilesets[tilesetIndex];
    if (!ts) return;

    this.tilesetIndex = tilesetIndex;
    this.tilesetImage = ts.image;

    this.tagger = new TileTagger({
      id: ts.name || `tileset_${tilesetIndex}`,
      name: ts.name || `Tileset ${tilesetIndex}`,
      tileSize: ts.tileWidth,
      columns: ts.columns || Math.floor(ts.image.width / ts.tileWidth),
      tileCount: ts.tileCount || (Math.floor(ts.image.width / ts.tileWidth) * Math.floor(ts.image.height / ts.tileHeight)),
    }, existingMeta);

    // Load tile properties from existing meta
    this._tileProperties.clear();
    if (existingMeta && existingMeta.tileProperties) {
      for (const [id, props] of Object.entries(existingMeta.tileProperties)) {
        this._tileProperties.set(Number(id), props);
      }
    }

    this.selectedTiles.clear();
    this._render();
  }

  /** Allow MenuBar to set a close callback. */
  setOnClose(fn) { this._onClose = fn; }

  // ─── Role definitions (shared across render methods) ─────────

  static ROLES = [
    { role: ROLE.TERRAIN, label: 'Terrain', color: '#2ecc71', desc: 'Ground tiles — grass, dirt, sand, water' },
    { role: ROLE.WALL, label: 'Wall', color: '#e74c3c', desc: 'Vertical surfaces — walls, cliffs, fences' },
    { role: ROLE.FLOOR, label: 'Floor', color: '#3498db', desc: 'Indoor walking surfaces — wood, stone, tile' },
    { role: ROLE.OBJECT, label: 'Object', color: '#9b59b6', desc: 'Multi-tile items — beds, tables, cars' },
    { role: ROLE.DECORATION, label: 'Decoration', color: '#f1c40f', desc: 'Small props — crates, barrels, flowers' },
    { role: ROLE.OPENING, label: 'Opening', color: '#e67e22', desc: 'Doors and windows' },
    { role: ROLE.OVERLAY, label: 'Overlay', color: '#1abc9c', desc: 'Drawn on top of other tiles — treetops, roof edges' },
  ];

  static STEPS = [
    { id: 'tag', num: '1', label: 'Tag Tiles', icon: '🏷' },
    { id: 'terrain', num: '2', label: 'Terrain Groups', icon: '🌿' },
    { id: 'structure', num: '3', label: 'Structures', icon: '🧱' },
    { id: 'objects', num: '4', label: 'Objects', icon: '📦' },
  ];

  // ─── Main Render ─────────────────────────────────────────────

  _render() {
    this.container.innerHTML = '';

    const panel = el('div', { className: 'tagger-panel' });

    // Top bar: title + close + progress
    panel.appendChild(this._renderTopBar());

    // Main content: canvas + sidebar
    const main = el('div', { className: 'tagger-main' });
    main.appendChild(this._renderCanvasArea());
    main.appendChild(this._renderSidebar());
    panel.appendChild(main);

    // Bottom action bar
    panel.appendChild(this._renderBottomBar());

    this.container.appendChild(panel);
    this._drawTileset();
  }

  // ─── Top Bar ─────────────────────────────────────────────────

  _renderTopBar() {
    const coverage = this.tagger.getCoverage();
    const bar = el('div', { className: 'tagger-topbar' });

    // Left: title
    const titleArea = el('div', { className: 'tagger-topbar-left' });
    titleArea.appendChild(el('h3', { className: 'tagger-title' }, 'Tile Tagger'));
    titleArea.appendChild(el('span', { className: 'tagger-tileset-name' }, this.tagger.tilesetInfo.name));
    bar.appendChild(titleArea);

    // Center: step tabs
    const steps = el('div', { className: 'tagger-steps' });
    for (const step of TileTaggerPanel.STEPS) {
      const isActive = this.currentStep === step.id;
      const btn = el('button', {
        className: `tagger-step ${isActive ? 'active' : ''}`,
        onClick: () => { this.currentStep = step.id; this._render(); },
      },
        el('span', { className: 'tagger-step-num' }, step.num),
        el('span', { className: 'tagger-step-label' }, step.label),
      );
      steps.appendChild(btn);
    }
    bar.appendChild(steps);

    // Right: progress + close
    const rightArea = el('div', { className: 'tagger-topbar-right' });
    const pct = coverage.percent;
    const progressWrap = el('div', { className: 'tagger-progress-wrap' });
    progressWrap.appendChild(el('div', { className: 'tagger-progress-label' }, `${coverage.tagged}/${coverage.total} tagged`));
    const progressBar = el('div', { className: 'tagger-progress-bar' });
    progressBar.appendChild(el('div', { className: 'tagger-progress-fill', style: { width: `${pct}%` } }));
    progressWrap.appendChild(progressBar);
    rightArea.appendChild(progressWrap);

    const closeBtn = el('button', {
      className: 'tagger-close-btn',
      onClick: () => { if (this._onClose) this._onClose(); },
    }, '\u00D7');
    rightArea.appendChild(closeBtn);

    bar.appendChild(rightArea);
    return bar;
  }

  // ─── Canvas Area (tileset display + legend) ──────────────────

  _renderCanvasArea() {
    const area = el('div', { className: 'tagger-canvas-area' });

    // Legend bar above canvas
    const legend = el('div', { className: 'tagger-legend' });
    for (const { label, color } of TileTaggerPanel.ROLES) {
      const swatch = el('span', { className: 'tagger-legend-item' },
        el('span', { className: 'tagger-legend-swatch', style: { background: color } }),
        el('span', {}, label),
      );
      legend.appendChild(swatch);
    }
    area.appendChild(legend);

    // Canvas
    const canvasWrap = el('div', { className: 'tagger-canvas-wrap' });
    const canvas = el('canvas', { className: 'tagger-canvas' });
    this._canvas = canvas;
    this._ctx = canvas.getContext('2d');

    const ts = this.state.get('tilesets')[this.tilesetIndex];
    const scale = 2;
    canvas.width = this.tilesetImage.width * scale;
    canvas.height = this.tilesetImage.height * scale;
    canvas.style.imageRendering = 'pixelated';

    canvas.addEventListener('click', (e) => this._handleCanvasClick(e, canvas, ts, scale));
    canvasWrap.appendChild(canvas);
    area.appendChild(canvasWrap);

    // Selection info below canvas
    if (this.selectedTiles.size > 0) {
      const ids = [...this.selectedTiles];
      const selBar = el('div', { className: 'tagger-sel-bar' },
        el('span', {}, `${ids.length} tile${ids.length > 1 ? 's' : ''} selected`),
        el('button', {
          className: 'tagger-sel-clear',
          onClick: () => { this.selectedTiles.clear(); this._render(); },
        }, 'Clear'),
      );
      area.appendChild(selBar);
    } else {
      area.appendChild(el('div', { className: 'tagger-sel-hint' },
        'Click a tile to select it. Shift+click to select multiple.',
      ));
    }

    return area;
  }

  _handleCanvasClick(e, canvas, ts, scale) {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const px = (e.clientX - rect.left) * scaleX;
    const py = (e.clientY - rect.top) * scaleY;
    const tileSize = (ts.tileWidth || 32) * scale;
    const col = Math.floor(px / tileSize);
    const row = Math.floor(py / tileSize);
    const columns = this.tagger.tilesetInfo.columns;
    const tileId = row * columns + col;

    if (tileId < 0 || tileId >= this.tagger.tilesetInfo.tileCount) return;

    // If autotile template is waiting for a tile, feed it there instead of selecting
    if (this._autotileTemplate && this._autotileTemplate.isWaitingForTile()) {
      this._autotileTemplate.assignTile(tileId);
      return;
    }

    if (e.shiftKey) {
      if (this.selectedTiles.has(tileId)) this.selectedTiles.delete(tileId);
      else this.selectedTiles.add(tileId);
    } else {
      this.selectedTiles.clear();
      this.selectedTiles.add(tileId);
    }
    this._drawTileset();
    this._updateSidebar();
  }

  _drawTileset() {
    if (!this._ctx || !this.tilesetImage) return;
    const ctx = this._ctx;
    const canvas = this._canvas;
    const img = this.tilesetImage;
    const scale = canvas.width / img.width;
    const tileSize = this.tagger.tilesetInfo.tileSize * scale;
    const columns = this.tagger.tilesetInfo.columns;

    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    // Role color map
    const ROLE_COLORS = {};
    for (const { role, color } of TileTaggerPanel.ROLES) {
      // Convert hex to rgba 0.35
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      ROLE_COLORS[role] = `rgba(${r},${g},${b},0.35)`;
    }

    for (let id = 0; id < this.tagger.tilesetInfo.tileCount; id++) {
      const col = id % columns;
      const row = Math.floor(id / columns);
      const x = col * tileSize;
      const y = row * tileSize;

      const role = this.tagger._tileRoles.get(id);
      if (role && ROLE_COLORS[role]) {
        ctx.fillStyle = ROLE_COLORS[role];
        ctx.fillRect(x, y, tileSize, tileSize);
      }

      if (this.selectedTiles.has(id)) {
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2;
        ctx.strokeRect(x + 1, y + 1, tileSize - 2, tileSize - 2);
        ctx.strokeStyle = '#3ba8ff';
        ctx.lineWidth = 1;
        ctx.strokeRect(x + 2, y + 2, tileSize - 4, tileSize - 4);
      }
    }

    // Grid
    ctx.strokeStyle = 'rgba(255,255,255,0.08)';
    ctx.lineWidth = 1;
    const rows = Math.ceil(this.tagger.tilesetInfo.tileCount / columns);
    for (let c = 0; c <= columns; c++) {
      ctx.beginPath();
      ctx.moveTo(c * tileSize, 0);
      ctx.lineTo(c * tileSize, rows * tileSize);
      ctx.stroke();
    }
    for (let r = 0; r <= rows; r++) {
      ctx.beginPath();
      ctx.moveTo(0, r * tileSize);
      ctx.lineTo(columns * tileSize, r * tileSize);
      ctx.stroke();
    }
  }

  // ─── Sidebar ─────────────────────────────────────────────────

  _renderSidebar() {
    const sidebar = el('div', { className: 'tagger-sidebar', id: 'taggerSidebar' });
    this._populateSidebar(sidebar);
    return sidebar;
  }

  _updateSidebar() {
    const sidebar = qs('#taggerSidebar', this.container);
    if (sidebar) {
      sidebar.innerHTML = '';
      this._populateSidebar(sidebar);
    }
  }

  _populateSidebar(sidebar) {
    switch (this.currentStep) {
      case 'tag': this._renderStepTag(sidebar); break;
      case 'terrain': this._renderStepTerrain(sidebar); break;
      case 'structure': this._renderStepStructure(sidebar); break;
      case 'objects': this._renderStepObjects(sidebar); break;
    }

    // Undo/redo at bottom
    if (this.tagger.undoCount > 0 || this.tagger.redoCount > 0) {
      const undoRow = el('div', { className: 'tagger-undo-row' });
      undoRow.appendChild(el('button', {
        className: 'tagger-btn',
        onClick: () => { this.tagger.undo(); this._render(); },
        disabled: this.tagger.undoCount === 0 ? 'true' : undefined,
      }, `Undo (${this.tagger.undoCount})`));
      undoRow.appendChild(el('button', {
        className: 'tagger-btn',
        onClick: () => { this.tagger.redo(); this._render(); },
        disabled: this.tagger.redoCount === 0 ? 'true' : undefined,
      }, `Redo (${this.tagger.redoCount})`));
      sidebar.appendChild(undoRow);
    }
  }

  // ─── Step 1: Tag Tiles ───────────────────────────────────────

  _renderStepTag(sidebar) {
    // Instructions
    sidebar.appendChild(el('div', { className: 'tagger-step-intro' },
      el('h4', {}, 'What is each tile?'),
      el('p', {}, 'Select one or more tiles on the left, then click a role below to tag them. This teaches the generator what each tile represents.'),
    ));

    // Selection status
    const selCount = this.selectedTiles.size;
    if (selCount === 0) {
      sidebar.appendChild(el('div', { className: 'tagger-empty-state' },
        'Select tiles from the tileset to get started.',
      ));
      return;
    }

    sidebar.appendChild(el('div', { className: 'tagger-sel-info' },
      `${selCount} tile${selCount > 1 ? 's' : ''} selected — assign a role:`,
    ));

    // Role buttons with descriptions
    const roleList = el('div', { className: 'tagger-role-list' });
    for (const { role, label, color, desc } of TileTaggerPanel.ROLES) {
      const btn = el('button', {
        className: 'tagger-role-btn',
        style: { borderLeftColor: color },
        onClick: () => {
          const ids = [...this.selectedTiles];
          if (ids.length === 1) this.tagger.tagTile(ids[0], role);
          else this.tagger.tagTiles(ids, role);
          this.selectedTiles.clear();
          this._render();
        },
      },
        el('span', { className: 'tagger-role-name' }, label),
        el('span', { className: 'tagger-role-desc' }, desc),
      );
      roleList.appendChild(btn);
    }
    sidebar.appendChild(roleList);

    // Single-tile properties panel
    if (this.selectedTiles.size === 1) {
      this._renderTileProperties(sidebar, [...this.selectedTiles][0]);
    }

    // Quick stats
    this._renderCoverageBreakdown(sidebar);
  }

  // ─── Tile Properties Panel ───────────────────────────────────

  _renderTileProperties(sidebar, tileId) {
    const props = this._tileProperties.get(tileId) || { passable: true, blocking: false, occluding: false, layer: 'ground' };
    const role = this.tagger._tileRoles.get(tileId);

    const panel = el('div', { className: 'tile-props' });
    panel.appendChild(el('div', { className: 'tile-props-title' }, 'Tile Properties'));

    // Preview + info
    const preview = el('div', { className: 'tile-props-preview' });
    const previewCanvas = el('canvas', { className: 'tile-props-canvas' });
    previewCanvas.width = 48;
    previewCanvas.height = 48;
    const ctx = previewCanvas.getContext('2d');
    ctx.imageSmoothingEnabled = false;
    const ts = this.state.get('tilesets')[this.tilesetIndex];
    const col = tileId % this.tagger.tilesetInfo.columns;
    const row = Math.floor(tileId / this.tagger.tilesetInfo.columns);
    ctx.drawImage(this.tilesetImage, col * ts.tileWidth, row * ts.tileHeight, ts.tileWidth, ts.tileHeight, 0, 0, 48, 48);
    preview.appendChild(previewCanvas);

    const info = el('div', {});
    info.appendChild(el('div', { className: 'tile-props-id' }, `Tile #${tileId}`));
    if (role) {
      const roleInfo = TileTaggerPanel.ROLES.find(r => r.role === role);
      info.appendChild(el('div', { className: 'tile-props-role', style: { color: roleInfo?.color || 'inherit' } }, roleInfo?.label || role));
    } else {
      info.appendChild(el('div', { className: 'tile-props-role', style: { color: 'var(--text-muted)' } }, 'Untagged'));
    }
    preview.appendChild(info);
    panel.appendChild(preview);

    // Property toggles
    const makeToggle = (label, key, value) => {
      const row = el('div', { className: 'tile-props-row' });
      row.appendChild(el('span', { className: 'tile-props-label' }, label));
      const toggle = el('input', {
        type: 'checkbox',
        className: 'tile-props-toggle',
      });
      toggle.checked = value;
      toggle.addEventListener('change', () => {
        const p = this._tileProperties.get(tileId) || { passable: true, blocking: false, occluding: false, layer: 'ground' };
        p[key] = toggle.checked;
        this._tileProperties.set(tileId, p);
      });
      row.appendChild(toggle);
      return row;
    };

    panel.appendChild(makeToggle('Passable', 'passable', props.passable));
    panel.appendChild(makeToggle('Blocks movement', 'blocking', props.blocking));
    panel.appendChild(makeToggle('Blocks vision', 'occluding', props.occluding));

    // Layer select
    const layerRow = el('div', { className: 'tile-props-row' });
    layerRow.appendChild(el('span', { className: 'tile-props-label' }, 'Layer'));
    const layerSelect = el('select', { className: 'tile-props-select' });
    for (const [val, label] of [['ground', 'Ground'], ['object', 'Object'], ['overhead', 'Overhead']]) {
      const opt = el('option', { value: val }, label);
      if (val === props.layer) opt.selected = true;
      layerSelect.appendChild(opt);
    }
    layerSelect.addEventListener('change', () => {
      const p = this._tileProperties.get(tileId) || { passable: true, blocking: false, occluding: false, layer: 'ground' };
      p.layer = layerSelect.value;
      this._tileProperties.set(tileId, p);
    });
    layerRow.appendChild(layerSelect);
    panel.appendChild(layerRow);

    sidebar.appendChild(panel);
  }

  // ─── Step 2: Terrain Groups ──────────────────────────────────

  _renderStepTerrain(sidebar) {
    sidebar.appendChild(el('div', { className: 'tagger-step-intro' },
      el('h4', {}, 'Group terrain variants'),
      el('p', {}, 'Create a terrain group, then click each slot in the template and click the matching tile in your tileset to assign it.'),
    ));

    // Create new group form
    const form = el('div', { className: 'tagger-form' });

    const nameInput = el('input', {
      type: 'text',
      className: 'tagger-input',
      placeholder: 'Terrain name (e.g., grass, water, dirt)',
    });
    form.appendChild(el('label', { className: 'tagger-field-label' }, 'Terrain name'));
    form.appendChild(nameInput);

    const formatSelect = el('select', { className: 'tagger-input' });
    formatSelect.appendChild(el('option', { value: 'simple' }, 'Simple — center tile + random variants'));
    formatSelect.appendChild(el('option', { value: 'edges', selected: true }, 'Edges — 3\u00D73 grid with corners & inner corners'));
    formatSelect.appendChild(el('option', { value: 'road' }, 'Road/path — straights, curves, T-junctions'));

    formatSelect.addEventListener('change', () => {
      this._createTerrainTemplate(nameInput.value.trim(), formatSelect.value);
      this._updateSidebar();
    });

    form.appendChild(el('label', { className: 'tagger-field-label' }, 'Edge style'));
    form.appendChild(formatSelect);

    // "Start mapping" button if no template active
    if (!this._autotileTemplate) {
      form.appendChild(el('button', {
        className: 'tagger-btn tagger-btn-primary',
        onClick: () => {
          if (!nameInput.value.trim()) {
            nameInput.focus();
            return;
          }
          this._createTerrainTemplate(nameInput.value.trim(), formatSelect.value);
          this._updateSidebar();
        },
      }, 'Start Mapping Tiles'));
    }

    sidebar.appendChild(form);

    // Show autotile template if active
    if (this._autotileTemplate) {
      sidebar.appendChild(this._autotileTemplate.render());

      // Variant tiles (simple mode: select extra tiles as random variants)
      if (this._autotileTemplate.format === 'simple') {
        const selCount = this.selectedTiles.size;
        if (selCount > 0) {
          sidebar.appendChild(el('div', { className: 'tagger-hint' },
            `${selCount} tile${selCount > 1 ? 's' : ''} selected — add as random variants:`,
          ));
          sidebar.appendChild(el('button', {
            className: 'tagger-btn',
            onClick: () => {
              // Store variants alongside the template
              if (!this._terrainVariants) this._terrainVariants = [];
              this._terrainVariants.push(...this.selectedTiles);
              this.selectedTiles.clear();
              this._render();
            },
          }, 'Add as Variants'));
        }
        if (this._terrainVariants && this._terrainVariants.length > 0) {
          sidebar.appendChild(el('div', { className: 'tagger-item-detail' },
            `${this._terrainVariants.length} variant tile(s) added`,
          ));
        }
      }

      // Save terrain group button
      const saveRow = el('div', { className: 'tagger-form', style: { marginTop: '8px' } });
      saveRow.appendChild(el('button', {
        className: 'tagger-btn tagger-btn-primary',
        onClick: () => this._saveTerrainGroup(),
      }, 'Save Terrain Group'));
      saveRow.appendChild(el('button', {
        className: 'tagger-btn',
        style: { marginTop: '4px' },
        onClick: () => {
          this._autotileTemplate = null;
          this._terrainName = null;
          this._terrainVariants = null;
          this._updateSidebar();
        },
      }, 'Cancel'));
      sidebar.appendChild(saveRow);
    }

    // Existing terrain groups
    const existing = this.tagger._terrainGroups;
    if (existing.size > 0) {
      sidebar.appendChild(el('div', { className: 'tagger-section-title' }, 'Defined terrain groups'));
      for (const [id, tg] of existing) {
        const varCount = (tg.randomVariants || []).length;
        const slotCount = Object.keys(tg.variants || {}).length;
        const detail = tg.autotileFormat === AUTOTILE_NONE
          ? `${1 + varCount} variant${varCount !== 0 ? 's' : ''}`
          : `${slotCount} edge tiles mapped`;
        const item = el('div', { className: 'tagger-item' },
          el('span', { className: 'tagger-item-name' }, id),
          el('span', { className: 'tagger-item-detail' }, detail),
        );
        sidebar.appendChild(item);
      }
    }
  }

  _createTerrainTemplate(name, format) {
    this._terrainName = name;
    this._terrainVariants = [];
    const ts = this.state.get('tilesets')[this.tilesetIndex];

    this._autotileTemplate = new AutotileTemplate({
      format,
      tilesetImage: this.tilesetImage,
      tileSize: ts.tileWidth,
      columns: this.tagger.tilesetInfo.columns,
      onSlotAssigned: (slotId, tileId) => {
        // Auto-tag the tile as terrain
        this.tagger.tagTile(tileId, ROLE.TERRAIN);
        this._drawTileset();
      },
    });
  }

  _saveTerrainGroup() {
    if (!this._autotileTemplate || !this._terrainName) return;

    const assignments = this._autotileTemplate.getAssignments();
    const centerTile = assignments.center;
    if (centerTile == null) {
      // Try to highlight the center slot
      return;
    }

    const format = this._autotileTemplate.format;
    const variants = this._terrainVariants || [];

    if (format === 'simple') {
      this.tagger.defineSimpleTerrain(this._terrainName, centerTile, variants);
    } else if (format === 'road') {
      const shapes = this._autotileTemplate.toBitmaskMap();
      this.tagger.defineRoadTerrain(this._terrainName, shapes);
    } else {
      // edges / full47 → blob terrain
      const bitmaskMap = this._autotileTemplate.toBitmaskMap();
      // Convert bitmask map to blob index map for defineBlobTerrain
      this.tagger.defineTerrainGroup({
        id: this._terrainName,
        autotileFormat: AUTOTILE_47,
        centerTile,
        variants: bitmaskMap,
        randomVariants: variants,
      });
    }

    // Cleanup
    this._autotileTemplate = null;
    this._terrainName = null;
    this._terrainVariants = null;
    this._render();
  }

  // ─── Step 3: Structures ──────────────────────────────────────

  _renderStepStructure(sidebar) {
    sidebar.appendChild(el('div', { className: 'tagger-step-intro' },
      el('h4', {}, 'Define walls, floors & doors'),
      el('p', {}, 'Tell the generator which tiles are walls, floors, and openings so it can build rooms and buildings correctly.'),
    ));

    // Sub-tabs for wall / floor / opening
    const subMode = this._structureSubMode || 'wall';
    const subTabs = el('div', { className: 'tagger-subtabs' });
    for (const [id, label] of [['wall', 'Walls'], ['floor', 'Floors'], ['opening', 'Doors & Windows']]) {
      subTabs.appendChild(el('button', {
        className: `tagger-subtab ${subMode === id ? 'active' : ''}`,
        onClick: () => { this._structureSubMode = id; this._updateSidebar(); },
      }, label));
    }
    sidebar.appendChild(subTabs);

    switch (subMode) {
      case 'wall': this._renderWallForm(sidebar); break;
      case 'floor': this._renderFloorForm(sidebar); break;
      case 'opening': this._renderOpeningForm(sidebar); break;
    }
  }

  _renderWallForm(sidebar) {
    sidebar.appendChild(el('div', { className: 'tagger-hint' },
      'Select 4+ wall tiles, then assign which direction each faces.',
    ));

    const ids = [...this.selectedTiles];
    const form = el('div', { className: 'tagger-form' });

    const nameInput = el('input', {
      type: 'text',
      className: 'tagger-input',
      placeholder: 'Wall set name (e.g., brick, stone)',
    });
    form.appendChild(el('label', { className: 'tagger-field-label' }, 'Wall set name'));
    form.appendChild(nameInput);

    const directions = ['top', 'bottom', 'left', 'right'];
    const selects = {};

    for (const dir of directions) {
      const row = el('div', { className: 'tagger-form-row' });
      row.appendChild(el('label', { className: 'tagger-field-label-inline' }, dir.charAt(0).toUpperCase() + dir.slice(1)));
      const select = el('select', { className: 'tagger-input-sm' });
      select.appendChild(el('option', { value: '' }, 'None'));
      for (const id of ids) select.appendChild(el('option', { value: String(id) }, `Tile ${id}`));
      row.appendChild(select);
      selects[dir] = select;
      form.appendChild(row);
    }

    form.appendChild(el('button', {
      className: 'tagger-btn tagger-btn-primary',
      disabled: ids.length < 2 ? 'true' : undefined,
      onClick: () => {
        if (!nameInput.value.trim()) return;
        const wallSet = { id: nameInput.value.trim() };
        for (const dir of directions) {
          const v = selects[dir].value;
          if (v) wallSet[dir] = Number(v);
        }
        this.tagger.defineWallSet(wallSet);
        this.selectedTiles.clear();
        this._render();
      },
    }, 'Create Wall Set'));

    sidebar.appendChild(form);

    // Existing
    if (this.tagger._wallSets.size > 0) {
      sidebar.appendChild(el('div', { className: 'tagger-section-title' }, 'Defined wall sets'));
      for (const [id] of this.tagger._wallSets) {
        sidebar.appendChild(el('div', { className: 'tagger-item' },
          el('span', { className: 'tagger-item-name' }, id),
        ));
      }
    }
  }

  _renderFloorForm(sidebar) {
    sidebar.appendChild(el('div', { className: 'tagger-hint' },
      'Select floor tiles, name the type, and optionally assign room zones.',
    ));

    const selCount = this.selectedTiles.size;
    const form = el('div', { className: 'tagger-form' });

    const nameInput = el('input', {
      type: 'text',
      className: 'tagger-input',
      placeholder: 'Floor name (e.g., wood, stone_tile)',
    });
    form.appendChild(el('label', { className: 'tagger-field-label' }, 'Floor name'));
    form.appendChild(nameInput);

    const zonesInput = el('input', {
      type: 'text',
      className: 'tagger-input',
      placeholder: 'Optional: bedroom, kitchen, hallway',
    });
    form.appendChild(el('label', { className: 'tagger-field-label' }, 'Used in rooms'));
    form.appendChild(zonesInput);

    form.appendChild(el('button', {
      className: 'tagger-btn tagger-btn-primary',
      disabled: selCount === 0 ? 'true' : undefined,
      onClick: () => {
        const ids = [...this.selectedTiles];
        if (ids.length === 0 || !nameInput.value.trim()) return;
        const zones = zonesInput.value.split(',').map(z => z.trim().toUpperCase()).filter(Boolean);
        this.tagger.defineFloorType({ id: nameInput.value.trim(), tile: ids[0], variants: ids.slice(1), zones });
        this.selectedTiles.clear();
        this._render();
      },
    }, selCount > 0 ? `Create floor from ${selCount} tile${selCount > 1 ? 's' : ''}` : 'Select tiles first'));

    sidebar.appendChild(form);

    if (this.tagger._floorTypes.size > 0) {
      sidebar.appendChild(el('div', { className: 'tagger-section-title' }, 'Defined floors'));
      for (const [id, ft] of this.tagger._floorTypes) {
        const varCount = (ft.variants || []).length;
        sidebar.appendChild(el('div', { className: 'tagger-item' },
          el('span', { className: 'tagger-item-name' }, id),
          el('span', { className: 'tagger-item-detail' }, `${1 + varCount} tile${varCount > 0 ? 's' : ''}`),
        ));
      }
    }
  }

  _renderOpeningForm(sidebar) {
    sidebar.appendChild(el('div', { className: 'tagger-hint' },
      'Select 1 tile for the closed state. Optionally shift+click a 2nd tile for the open state.',
    ));

    const selCount = this.selectedTiles.size;
    const form = el('div', { className: 'tagger-form' });

    const nameInput = el('input', {
      type: 'text',
      className: 'tagger-input',
      placeholder: 'Name (e.g., wooden_door, window)',
    });
    form.appendChild(el('label', { className: 'tagger-field-label' }, 'Opening name'));
    form.appendChild(nameInput);

    const typeSelect = el('select', { className: 'tagger-input' });
    typeSelect.appendChild(el('option', { value: 'door' }, 'Door'));
    typeSelect.appendChild(el('option', { value: 'window' }, 'Window'));
    form.appendChild(el('label', { className: 'tagger-field-label' }, 'Type'));
    form.appendChild(typeSelect);

    form.appendChild(el('button', {
      className: 'tagger-btn tagger-btn-primary',
      disabled: selCount === 0 ? 'true' : undefined,
      onClick: () => {
        const ids = [...this.selectedTiles];
        if (ids.length === 0 || !nameInput.value.trim()) return;
        const type = typeSelect.value;
        const options = {};
        if (ids.length > 1) {
          if (type === 'door') options.tileOpen = ids[1];
          else options.tileBroken = ids[1];
        }
        this.tagger.defineOpening(nameInput.value.trim(), type, ids[0], options);
        this.selectedTiles.clear();
        this._render();
      },
    }, selCount > 0 ? 'Create Opening' : 'Select a tile first'));

    sidebar.appendChild(form);

    if (this.tagger._openings.size > 0) {
      sidebar.appendChild(el('div', { className: 'tagger-section-title' }, 'Defined openings'));
      for (const [id, op] of this.tagger._openings) {
        sidebar.appendChild(el('div', { className: 'tagger-item' },
          el('span', { className: 'tagger-item-name' }, id),
          el('span', { className: 'tagger-item-detail' }, op.type),
        ));
      }
    }
  }

  // ─── Step 4: Objects & Decorations ───────────────────────────

  _renderStepObjects(sidebar) {
    sidebar.appendChild(el('div', { className: 'tagger-step-intro' },
      el('h4', {}, 'Define objects & decorations'),
      el('p', {}, 'Tag multi-tile objects (furniture, trees) and single-tile decorations (flowers, barrels) so the generator can place them in rooms and outdoor areas.'),
    ));

    const subMode = this._objectSubMode || 'object';
    const subTabs = el('div', { className: 'tagger-subtabs' });
    for (const [id, label] of [['object', 'Multi-Tile Objects'], ['decoration', 'Decorations']]) {
      subTabs.appendChild(el('button', {
        className: `tagger-subtab ${subMode === id ? 'active' : ''}`,
        onClick: () => { this._objectSubMode = id; this._updateSidebar(); },
      }, label));
    }
    sidebar.appendChild(subTabs);

    if (subMode === 'object') {
      this._renderObjectForm(sidebar);
    } else {
      this._renderDecorationForm(sidebar);
    }
  }

  _renderObjectForm(sidebar) {
    sidebar.appendChild(el('div', { className: 'tagger-hint' },
      'Select the top-left tile of the object, then set its width and height in tiles.',
    ));

    const selCount = this.selectedTiles.size;
    const form = el('div', { className: 'tagger-form' });

    const nameInput = el('input', {
      type: 'text',
      className: 'tagger-input',
      placeholder: 'Object name (e.g., double_bed, oak_tree)',
    });
    form.appendChild(el('label', { className: 'tagger-field-label' }, 'Object name'));
    form.appendChild(nameInput);

    const sizeRow = el('div', { className: 'tagger-form-row' });
    const wInput = el('input', { type: 'number', className: 'tagger-input-sm', value: '2', min: '1', max: '10' });
    const hInput = el('input', { type: 'number', className: 'tagger-input-sm', value: '2', min: '1', max: '10' });
    sizeRow.appendChild(el('label', { className: 'tagger-field-label-inline' }, 'Size'));
    sizeRow.appendChild(wInput);
    sizeRow.appendChild(el('span', { className: 'tagger-x' }, '\u00D7'));
    sizeRow.appendChild(hInput);
    sizeRow.appendChild(el('span', { className: 'tagger-size-unit' }, 'tiles'));
    form.appendChild(sizeRow);

    const zonesInput = el('input', {
      type: 'text',
      className: 'tagger-input',
      placeholder: 'Optional: bedroom, living_room',
    });
    form.appendChild(el('label', { className: 'tagger-field-label' }, 'Placed in rooms'));
    form.appendChild(zonesInput);

    form.appendChild(el('button', {
      className: 'tagger-btn tagger-btn-primary',
      disabled: selCount === 0 ? 'true' : undefined,
      onClick: () => {
        const ids = [...this.selectedTiles];
        if (ids.length === 0 || !nameInput.value.trim()) return;
        const zones = zonesInput.value.split(',').map(z => z.trim().toUpperCase()).filter(Boolean);
        this.tagger.defineObject(nameInput.value.trim(), ids[0], Number(wInput.value), Number(hInput.value), { zones });
        this.selectedTiles.clear();
        this._render();
      },
    }, selCount > 0 ? 'Create Object' : 'Select top-left tile first'));

    sidebar.appendChild(form);

    if (this.tagger._objects.size > 0) {
      sidebar.appendChild(el('div', { className: 'tagger-section-title' }, 'Defined objects'));
      for (const [id, obj] of this.tagger._objects) {
        sidebar.appendChild(el('div', { className: 'tagger-item' },
          el('span', { className: 'tagger-item-name' }, id),
          el('span', { className: 'tagger-item-detail' }, `${obj.footprint.w}\u00D7${obj.footprint.h}`),
        ));
      }
    }
  }

  _renderDecorationForm(sidebar) {
    sidebar.appendChild(el('div', { className: 'tagger-hint' },
      'Select one or more single-tile decorations. These are small props the generator scatters around.',
    ));

    const selCount = this.selectedTiles.size;
    const form = el('div', { className: 'tagger-form' });

    const nameInput = el('input', {
      type: 'text',
      className: 'tagger-input',
      placeholder: 'Name (e.g., crate, barrel, flower)',
    });
    form.appendChild(el('label', { className: 'tagger-field-label' }, 'Decoration name'));
    form.appendChild(nameInput);

    const zonesInput = el('input', {
      type: 'text',
      className: 'tagger-input',
      placeholder: 'Optional: storage, outdoor, garden',
    });
    form.appendChild(el('label', { className: 'tagger-field-label' }, 'Placed in areas'));
    form.appendChild(zonesInput);

    // Checkboxes
    const checks = el('div', { className: 'tagger-checks' });
    const blockCheck = el('input', { type: 'checkbox', id: 'decoBlocking' });
    const outdoorCheck = el('input', { type: 'checkbox', id: 'decoOutdoor' });
    const scatterCheck = el('input', { type: 'checkbox', id: 'decoScatter' });
    checks.appendChild(el('label', { className: 'tagger-check-label' }, blockCheck, ' Blocks movement'));
    checks.appendChild(el('label', { className: 'tagger-check-label' }, outdoorCheck, ' Outdoor'));
    checks.appendChild(el('label', { className: 'tagger-check-label' }, scatterCheck, ' Scatter randomly'));
    form.appendChild(checks);

    form.appendChild(el('button', {
      className: 'tagger-btn tagger-btn-primary',
      disabled: selCount === 0 ? 'true' : undefined,
      onClick: () => {
        const ids = [...this.selectedTiles];
        if (ids.length === 0 || !nameInput.value.trim()) return;
        const zones = zonesInput.value.split(',').map(z => z.trim().toUpperCase()).filter(Boolean);
        for (const tileId of ids) {
          this.tagger.defineDecoration(
            ids.length === 1 ? nameInput.value.trim() : `${nameInput.value.trim()}_${tileId}`,
            tileId, zones, {
              blocking: blockCheck.checked,
              outdoor: outdoorCheck.checked,
              indoor: !outdoorCheck.checked,
              scatter: scatterCheck.checked,
            }
          );
        }
        this.selectedTiles.clear();
        this._render();
      },
    }, selCount > 0 ? `Create from ${selCount} tile${selCount > 1 ? 's' : ''}` : 'Select tiles first'));

    sidebar.appendChild(form);

    if (this.tagger._decorations.size > 0) {
      sidebar.appendChild(el('div', { className: 'tagger-section-title' }, 'Defined decorations'));
      for (const [id] of this.tagger._decorations) {
        sidebar.appendChild(el('div', { className: 'tagger-item' },
          el('span', { className: 'tagger-item-name' }, id),
        ));
      }
    }
  }

  // ─── Coverage breakdown ──────────────────────────────────────

  _renderCoverageBreakdown(sidebar) {
    const coverage = this.tagger.getCoverage();
    if (coverage.tagged === 0) return;

    const breakdown = el('div', { className: 'tagger-breakdown' });
    breakdown.appendChild(el('div', { className: 'tagger-section-title' }, 'Tagged so far'));

    const rows = [
      ['Terrain', coverage.byRole.terrain, '#2ecc71'],
      ['Wall', coverage.byRole.wall, '#e74c3c'],
      ['Floor', coverage.byRole.floor, '#3498db'],
      ['Object', coverage.byRole.object, '#9b59b6'],
      ['Decoration', coverage.byRole.decoration, '#f1c40f'],
    ];

    for (const [label, count, color] of rows) {
      if (count === 0) continue;
      breakdown.appendChild(el('div', { className: 'tagger-breakdown-row' },
        el('span', { className: 'tagger-breakdown-swatch', style: { background: color } }),
        el('span', {}, `${label}: ${count}`),
      ));
    }

    sidebar.appendChild(breakdown);
  }

  // ─── Bottom Bar ──────────────────────────────────────────────

  _renderBottomBar() {
    const bar = el('div', { className: 'tagger-bottombar' });

    bar.appendChild(el('button', {
      className: 'tagger-btn tagger-btn-save',
      onClick: () => this._save(),
    }, 'Save'));

    bar.appendChild(el('button', {
      className: 'tagger-btn',
      onClick: () => this._export(),
    }, 'Export'));

    bar.appendChild(el('button', {
      className: 'tagger-btn',
      onClick: () => this._import(),
    }, 'Import'));

    // Spacer
    bar.appendChild(el('div', { style: { flex: '1' } }));

    // Missing requirements as compact warnings
    const missing = this.tagger.getMissingRequirements();
    if (missing.length > 0) {
      const count = missing.length;
      bar.appendChild(el('span', { className: 'tagger-missing-badge' }, `${count} item${count > 1 ? 's' : ''} needed`));
    }

    return bar;
  }

  // ─── Save / Export / Import ──────────────────────────────────

  _save() {
    const meta = this.tagger.build();
    const json = meta.toJSON();
    // Attach tile properties
    if (this._tileProperties.size > 0) {
      json.tileProperties = {};
      for (const [id, props] of this._tileProperties) {
        json.tileProperties[id] = props;
      }
    }
    const tilesets = this.state.get('tilesets');
    if (tilesets[this.tilesetIndex]) {
      tilesets[this.tilesetIndex].tileMeta = json;
      this.state.set('tilesets', tilesets);
    }
    // Brief visual feedback
    const saveBtn = qs('.tagger-btn-save', this.container);
    if (saveBtn) {
      const orig = saveBtn.textContent;
      saveBtn.textContent = 'Saved!';
      saveBtn.classList.add('tagger-btn-saved');
      setTimeout(() => { saveBtn.textContent = orig; saveBtn.classList.remove('tagger-btn-saved'); }, 1500);
    }
  }

  _export() {
    const json = JSON.stringify(this.tagger.toJSON(), null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${this.tagger.tilesetInfo.id}.tilemeta.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  _import() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,.tilemeta.json';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const text = await file.text();
      try {
        const data = JSON.parse(text);
        this.open(this.tilesetIndex, data);
      } catch (err) {
        console.error('Failed to import tile data:', err);
      }
    };
    input.click();
  }
}
